import _flatten from 'lodash/flatten';
import _partition from 'lodash/partition';
import _prop from 'lodash/property';
import _sortBy from 'lodash/sortBy';
import queryString from 'qs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppName } from 'sl-api-connector';
import { SlDropdownMenuOption } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlMenu';
import { SlDropdownMenu } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/input';
import { updateQueryParams } from 'src/store/modules/app/operations';
import { updateAllTimePeriods } from 'src/store/modules/main-time-period/operations';
import { getInitialState as getDefaultTimePeriods } from 'src/store/modules/main-time-period/reducers';
import { QueryResponse } from 'src/types/application/types';
import { AvailableRetailer } from 'src/types/store/storeTypes';
import { useAppSelector, useLocation } from 'src/utils/Hooks';
import { getSearchAppName, usesCustomTimePeriods, usesSecondaryRetailers } from 'src/utils/app';
import { computeComparisonTimePeriod, computeMainTimePeriod } from 'src/utils/dateformatting';

const SelectRetailerDropdown = () => {
  const locationChange = useLocation();
  const dispatch = useDispatch();
  const { location } = window;
  const parentPlatform = useAppSelector((state) => state.parentPlatform);
  const app = useAppSelector((state) => state.app);
  const user = useAppSelector((state) => state.user);
  const retailer = useAppSelector((state) => state.retailer);
  const allWeekIdsByRetailerId = useAppSelector((state) => state.allWeekIdsByRetailerId);
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const { availableRetailers } = retailer;

  const [newOptions, setNewOptions] = useState<SlDropdownMenuOption[]>([]);

  const isRetailerActive = useCallback(
    (r: AvailableRetailer): boolean => {
      if (user.config.secondaryRetailerIds && usesSecondaryRetailers()) {
        return (
          user.config.secondaryRetailerIds.includes(parseInt(r.id, 10)) &&
          user.config.allWeekIdsByRetailerId[+r.id] &&
          r.supportedAppNames.includes(app.name)
        );
      }

      return (
        user.config.allRetailerIds.includes(parseInt(r.id, 10)) &&
        user.config.allWeekIdsByRetailerId[+r.id] &&
        r.supportedAppNames.includes(app.name)
      );
    },
    [app.name, user.config.allRetailerIds, user.config.allWeekIdsByRetailerId, user.config.secondaryRetailerIds]
  );

  const isAdRetailerActive = useCallback(
    (r: AvailableRetailer): boolean => {
      if (user.config.secondaryRetailerIds && usesSecondaryRetailers()) {
        return (
          user.config.secondaryRetailerIds.includes(parseInt(r.id, 10)) &&
          user.config.allWeekIdsByRetailerId[+r.id] &&
          r.supportedAppNames.includes(AppName.Advertising)
        );
      }

      return (
        user.config.allRetailerIds.includes(parseInt(r.id, 10)) &&
        user.config.allWeekIdsByRetailerId[+r.id] &&
        r.supportedAppNames.includes(AppName.Advertising)
      );
    },
    [user.config.allRetailerIds, user.config.allWeekIdsByRetailerId, user.config.secondaryRetailerIds]
  );

  const availableRetailersWithActiveProp = useMemo(() => {
    return availableRetailers.map((val) => ({
      ...val,
      active: isRetailerActive(val)
    }));
  }, [availableRetailers, isRetailerActive]);

  // Sort active retailers on top of inactive retailers with each (active and inactive) sorted by display name
  const orderedRetailers = useMemo(() => {
    return _flatten(
      _partition(availableRetailersWithActiveProp, _prop('active')).map((coll) => _sortBy(coll, 'displayName'))
    );
  }, [availableRetailersWithActiveProp]);

  const handleRetailerChange = (retailerSelection: SlDropdownMenuOption) => {
    /**
     * The newly selected retailer ID
     */
    const retailerId = retailerSelection.id;
    const queryParams = queryString.parse(location.search, { ignoreQueryPrefix: true, arrayLimit: 100 });
    const { availableMainTimePeriods } = usesCustomTimePeriods() ? getDefaultTimePeriods() : mainTimePeriod;
    const { availableComparisonTimePeriods } = comparisonTimePeriod;
    const allWeekIds = allWeekIdsByRetailerId[retailerId];

    const comparisonTimePeriodId =
      comparisonTimePeriod && comparisonTimePeriod.id !== undefined
        ? comparisonTimePeriod.id
        : availableComparisonTimePeriods[0].id;

    // Default to first app default time period if using secondary retailers
    const mainPeriodIndex = usesCustomTimePeriods()
      ? availableMainTimePeriods.findIndex((period) => period.default)
      : mainTimePeriod.availableMainTimePeriods.findIndex((x) => x.id === mainTimePeriod.id);
    const newTimePeriod = computeMainTimePeriod(allWeekIds, availableMainTimePeriods[mainPeriodIndex], app);
    const compPeriodId = availableComparisonTimePeriods.find((x) => x.id === comparisonTimePeriodId)!.id;
    const updatedComparisonPeriod = computeComparisonTimePeriod(allWeekIds, newTimePeriod, compPeriodId);

    updatedComparisonPeriod.comparisonIndex = availableComparisonTimePeriods.findIndex(
      (x) => x.id === comparisonTimePeriodId
    );

    // If the user is on beacon pro and the retailer is not beacon pro enabled, navigate to old beacon
    dispatch(updateAllTimePeriods(allWeekIds, newTimePeriod, updatedComparisonPeriod));
    dispatch(updateQueryParams(app, { id: retailerId }, newTimePeriod, updatedComparisonPeriod, queryParams)).then(
      (response: QueryResponse) => {
        const { additionalParams, dropDownSelectionParams, nonPersistentParams, searchParams } = response.params;
        let customParams = queryParams.tab || queryParams.entityType ? dropDownSelectionParams : additionalParams;
        // Go back to home page if retailer changes on ad manager
        if (app.name === 'advertising') {
          location.pathname = '/overview';
          customParams = '&tab=adManager&subtab=keyMetrics';
        }
        return window.open(`${location.pathname}${searchParams}${customParams}${nonPersistentParams}`, '_self');
      }
    );
    return null;
  };

  useEffect(() => {
    const searchAppName = getSearchAppName();
    let options = [];

    if (searchAppName === AppName.Advertising) {
      // if search app name is advertising, option will be parent platform + retailer name

      const platforms = parentPlatform?.availablePlatforms;

      options = orderedRetailers
        .filter((val) => {
          if (retailer.id === '0') {
            return val.id === '0';
          }
          return val.id !== '0';
        })
        .filter((val) => val.supportedAppNames.includes(AppName.Advertising))
        .map((rt) => {
          const currentParentPlatform = platforms.find((platform) => {
            return platform.retailers.find((r) => r.extendedAttributes.retailerId === +rt.id);
          });
          if (!currentParentPlatform || currentParentPlatform?.name === 'Amazon') {
            return {
              label: rt.displayName,
              id: rt.id,
              disabled: !isAdRetailerActive(rt)
            };
          }
          return {
            label: `${currentParentPlatform?.name} - ${rt.displayName}`,
            id: rt.id,
            disabled: !isAdRetailerActive(rt)
          };
        });
    } else {
      // else it will be retailer name
      options = orderedRetailers
        .filter((val) => {
          if (retailer.id === '0') {
            return val.id === '0';
          }
          return val.id !== '0';
        })
        .map((rt) => ({ label: rt.displayName, id: rt.id, disabled: !rt.active }));
    }

    setNewOptions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedRetailers, retailer.id, app.displayName, locationChange]);

  const handleChange = (retailerSelection: SlDropdownMenuOption) => {
    if (!retailerSelection.disabled) {
      handleRetailerChange(retailerSelection);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        height: 30
      }}
    >
      <SlDropdownMenu
        amountOfOptionsToShow={4}
        options={newOptions}
        defaultLabel={retailer.displayName}
        selectedId={retailer.id}
        onChange={(r) => handleChange(r)}
        buttonVariant="titleSmall"
        iconStyle={{ marginLeft: '8px' }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        // Allows for truncation of retailer names
        menuStyles={{ width: '160px' }}
      />
    </div>
  );
};

export default SelectRetailerDropdown;
